import type { PushSubscriptionPayload } from 'shared/hooks/api/pushSubscriptions';

export type PushStatus =
  | 'unsupported'
  | 'needs-install'
  | 'blocked'
  | 'off'
  | 'on';

interface PushStatusInput {
  isSupported: boolean;
  isIos: boolean;
  isInStandaloneMode: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
}

/**
 * Decides what the device can do with push. An iPhone can only receive push
 * from the home screen app, so a Safari tab there needs the install first,
 * even though the browser reports push as missing.
 */
export function getPushStatus({
  isSupported,
  isIos,
  isInStandaloneMode,
  permission,
  isSubscribed,
}: PushStatusInput): PushStatus {
  if (isIos && !isInStandaloneMode) return 'needs-install';
  if (!isSupported) return 'unsupported';
  if (permission === 'denied') return 'blocked';
  if (permission === 'granted' && isSubscribed) return 'on';

  return 'off';
}

export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * The browser wants the VAPID public key as bytes, the API sends it as
 * base64url text without padding.
 */
export function base64UrlToUint8Array(value: string): Uint8Array {
  const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;

  const registration = await navigator.serviceWorker.ready;

  return registration.pushManager.getSubscription();
}

export async function subscribeToPush(
  vapidPublicKey: string
): Promise<PushSubscription> {
  const registration = await navigator.serviceWorker.ready;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToUint8Array(vapidPublicKey),
  });
}

export function toPayload(
  subscription: PushSubscription
): PushSubscriptionPayload {
  const { endpoint, keys } = subscription.toJSON();

  return {
    endpoint: endpoint ?? subscription.endpoint,
    keys: { p256dh: keys?.p256dh ?? '', auth: keys?.auth ?? '' },
  };
}
